import { InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { useMemo } from 'react';
import type { UnitConverter } from '/@/core/units';
import { _ } from '/@/locale';
import type { CropType } from '/@/types/crop-monitoring';
import { getDensityRange } from '../types';

interface DensityInputProps {
    value: number | undefined;
    onChange: (value: number | undefined) => void;
    units: UnitConverter;
    crop: CropType | 'other' | '';
    error?: string;
}

const DensityInput = ({ value, onChange, units, crop, error }: DensityInputProps) => {
    const densityCrop = crop || 'corn';
    const { min: minDensity, max: maxDensity } = getDensityRange(crop);

    const handleChange = (inputValue: string) => {
        if (!inputValue) {
            onChange(undefined);
            return;
        }
        onChange(units.fromDensity(inputValue, densityCrop));
    };

    const validationError = useMemo(() => {
        // 0 is a value the user typed, not an empty field: it has to fail the min check.
        if (value == null) return null;
        if (value < minDensity) {
            return _('La densidad debe ser mayor a {{minDensity}}', {
                minDensity: units.toDensity(minDensity, densityCrop, 0).localeValue,
            });
        }
        if (value > maxDensity) {
            return _('La densidad debe ser menor a {{maxDensity}}', {
                maxDensity: units.toDensity(maxDensity, densityCrop, 0).localeValue,
            });
        }
        return null;
    }, [value, units, densityCrop, minDensity, maxDensity]);

    const densityUnit = units.getUnit('density', densityCrop, 'planting');
    const exampleDensity = units.toDensity((minDensity + maxDensity) / 2, densityCrop, 0).localeValue;

    return (
        <Stack spacing={0.5} sx={{ width: '100%' }}>
            <Typography variant='subtitle1' color='primary' fontWeight='bold'>
                {_('Densidad')}
            </Typography>
            <TextField
                placeholder={_('Ej. {{example}} {{unit}}', { example: exampleDensity, unit: densityUnit })}
                value={value != null ? (units.toDensity(value, densityCrop, 0).value ?? '') : ''}
                onChange={(e) => handleChange(e.target.value)}
                fullWidth
                type='number'
                error={!!error || !!validationError}
                helperText={
                    error ||
                    validationError ||
                    _('Rango de {{minDensity}} a {{maxDensity}} {{unit}}', {
                        minDensity: units.toDensity(minDensity, densityCrop, 0).localeValue,
                        maxDensity: units.toDensity(maxDensity, densityCrop, 0).localeValue,
                        unit: densityUnit,
                    })
                }
                sx={{
                    background: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                        top: '-10px',
                    },
                }}
                inputProps={{ 'data-testid': 'field-monitoring-creation-dialog-density-input' }}
                InputProps={{
                    endAdornment: <InputAdornment position='end'>{densityUnit}</InputAdornment>,
                }}
            />
        </Stack>
    );
};

export default DensityInput;
